import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axios from "../../axios/axiosconf";
import SearchableSelect from "./SearchableSelect";
import ModalCIE10 from "./ModalCIE10";

const EMPTY_CONSULTA = {
    modalidadGrupoServicioTecSal: "01",
    grupoServicios: "01",
    codServicio: "",
    finalidadTecnologiaSalud: "",
    causaMotivoAtencion: "",
    codDiagnosticoPrincipal: "",
    codDiagnosticoRelacionado1: "",
    codDiagnosticoRelacionado2: "",
    codDiagnosticoRelacionado3: "",
    conceptoRecaudo: "05",
    valorPagoModerador: "0",
    activo: 1,
};

const EMPTY_PROCEDIMIENTO = {
    modalidadGrupoServicioTecSal: "01",
    grupoServicios: "01",
    codServicio: "",
    finalidadTecnologiaSalud: "",
    codDiagnosticoPrincipal: "",
    codDiagnosticoRelacionado: "",
    codComplicacion: "",
    conceptoRecaudo: "05",
    valorPagoModerador: "0",
    activo: 1,
};

/**
 * Modal rápido para crear plantilla RIPS cuando ya se conoce tipo + CUPS
 * (p. ej. desde el selector de servicios en registro RIPS).
 */
function ModalPlantillaRipsRapida({ show, onClose, onSaved, servicio, tipo = "consulta" }) {
    const [form, setForm] = useState({ ...EMPTY_CONSULTA });
    const [serviciosRips, setServiciosRips] = useState([]);
    const [finalidades, setFinalidades] = useState([]);
    const [causas, setCausas] = useState([]);
    const [cie10Field, setCie10Field] = useState("");
    const [showCie10, setShowCie10] = useState(false);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState("");

    useEffect(() => {
        if (!show || !servicio) return;
        setMsg("");
        setForm(tipo === "procedimiento" ? { ...EMPTY_PROCEDIMIENTO } : { ...EMPTY_CONSULTA });

        let userdata = {};
        try {
            userdata = JSON.parse(localStorage.getItem("userdata") || "{}");
        } catch (e) {
            userdata = {};
        }
        const uid = userdata.user_id;
        if (!uid) return;

        axios.get("/rips-servicios?uid=" + uid).then((r) => setServiciosRips(r.data || [])).catch(() => {});
        axios.get("/rips-finalidad-consulta?uid=" + uid).then((r) => setFinalidades(r.data || [])).catch(() => {});
        if (tipo === "consulta") {
            axios.get("/rips-causa-externa?uid=" + uid).then((r) => setCausas(r.data || [])).catch(() => {});
        }
    }, [show, servicio, tipo]);

    const campoCie = (name, label) => (
        <div className="form-group">
            <label>{label}</label>
            <div className="input-group">
                <input type="text" className="form-control" value={form[name] || ""} readOnly />
                <div className="input-group-append">
                    {form[name] && (
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => setForm((p) => ({ ...p, [name]: "" }))}
                            title="Limpiar"
                        >
                            <i className="fas fa-times" />
                        </button>
                    )}
                    <button
                        type="button"
                        className="btn btn-info"
                        onClick={() => {
                            setCie10Field(name);
                            setShowCie10(true);
                        }}
                    >
                        <i className="fas fa-search" />
                    </button>
                </div>
            </div>
        </div>
    );

    const guardar = (e) => {
        e.preventDefault();
        if (!servicio?.id || !servicio?.codigorips) {
            setMsg("Servicio o CUPS no válido.");
            return;
        }
        setSaving(true);
        setMsg("");
        const payload = {
            ...form,
            tipo,
            lista_precios_id: String(servicio.id),
            cups: String(servicio.codigorips).trim(),
            valorPagoModerador:
                form.valorPagoModerador === "" || form.valorPagoModerador == null
                    ? 0
                    : Number(form.valorPagoModerador) || 0,
        };
        axios
            .post("/parametrizar-servicios-rips", payload)
            .then((r) => {
                const plantillaId = r.data?.id || r.data?.plantilla_id || true;
                if (typeof onSaved === "function") {
                    onSaved(servicio, plantillaId);
                }
                onClose();
            })
            .catch((err) => setMsg(err.response?.data?.message || "No se pudo guardar la plantilla."))
            .finally(() => setSaving(false));
    };

    if (!show || !servicio) return null;

    return createPortal(
        <>
            <div
                className="modal fade show"
                style={{ display: "block", backgroundColor: "rgba(0,0,0,0.45)", zIndex: 20100 }}
                tabIndex="-1"
                onMouseDown={(e) => e.stopPropagation()}
            >
                <div className="modal-dialog modal-lg" style={{ maxHeight: "92vh", margin: "1.5rem auto" }}>
                    <div
                        className="modal-content"
                        style={{ maxHeight: "92vh", display: "flex", flexDirection: "column" }}
                    >
                        <div className="modal-header" style={{ flex: "0 0 auto" }}>
                            <h5 className="modal-title">
                                Plantilla {tipo === "procedimiento" ? "procedimiento" : "consulta"}
                            </h5>
                            <button type="button" className="close" onClick={onClose}>
                                <span>&times;</span>
                            </button>
                        </div>
                        <form
                            onSubmit={guardar}
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                flex: "1 1 auto",
                                minHeight: 0,
                                overflow: "hidden",
                            }}
                        >
                            <div
                                className="modal-body"
                                style={{
                                    overflowY: "auto",
                                    flex: "1 1 auto",
                                    maxHeight: "calc(92vh - 140px)",
                                    WebkitOverflowScrolling: "touch",
                                }}
                            >
                                {msg && <div className="alert alert-danger py-2">{msg}</div>}

                                <div className="alert alert-light border py-2 mb-3">
                                    <strong>{servicio.nombre || "Servicio"}</strong>
                                    <span className="ml-2 badge badge-info">CUPS {servicio.codigorips}</span>
                                </div>

                                <div className="form-group">
                                    <label>Código CUPS</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={servicio.codigorips || ""}
                                        readOnly
                                        disabled
                                    />
                                </div>

                                <h6 className="text-muted mb-3">Datos de plantilla</h6>

                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label>Modalidad</label>
                                            <select
                                                className="form-control"
                                                value={form.modalidadGrupoServicioTecSal}
                                                onChange={(e) =>
                                                    setForm((p) => ({
                                                        ...p,
                                                        modalidadGrupoServicioTecSal: e.target.value,
                                                    }))
                                                }
                                            >
                                                <option value="01">INTRAMURAL</option>
                                                <option value="02">EXTRAMURAL UNIDAD MÓVIL</option>
                                                <option value="03">EXTRAMURAL DOMICILIARIA</option>
                                                <option value="04">EXTRAMURAL JORNADA DE SALUD</option>
                                                <option value="06">TELEMEDICINA INTERACTIVA</option>
                                                <option value="07">TELEMEDICINA NO INTERACTIVA</option>
                                                <option value="08">TELEMEDICINA TELEXPERTICIA</option>
                                                <option value="09">TELEMEDICINA TELEMONITOREO</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label>Grupo de servicios</label>
                                            <select
                                                className="form-control"
                                                value={form.grupoServicios}
                                                onChange={(e) =>
                                                    setForm((p) => ({ ...p, grupoServicios: e.target.value }))
                                                }
                                            >
                                                <option value="01">CONSULTA EXTERNA</option>
                                                <option value="02">APOYO DIAGNÓSTICO Y COMPLEMENTACIÓN TERAPÉUTICA</option>
                                                <option value="03">INTERNACIÓN</option>
                                                <option value="04">QUIRÚRGICO</option>
                                                <option value="05">ATENCIÓN INMEDIATA</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>Servicio RIPS (catálogo)</label>
                                    <SearchableSelect
                                        name="codServicio"
                                        value={form.codServicio}
                                        onChange={(e) => setForm((p) => ({ ...p, codServicio: e.target.value }))}
                                        options={serviciosRips}
                                        placeholder="Seleccione servicio…"
                                        menuZIndex={20500}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Finalidad</label>
                                    <SearchableSelect
                                        name="finalidadTecnologiaSalud"
                                        value={form.finalidadTecnologiaSalud}
                                        onChange={(e) =>
                                            setForm((p) => ({ ...p, finalidadTecnologiaSalud: e.target.value }))
                                        }
                                        options={finalidades}
                                        placeholder="Seleccione finalidad…"
                                        menuZIndex={20500}
                                    />
                                </div>

                                {tipo === "consulta" && (
                                    <div className="form-group">
                                        <label>Causa externa</label>
                                        <SearchableSelect
                                            name="causaMotivoAtencion"
                                            value={form.causaMotivoAtencion}
                                            onChange={(e) =>
                                                setForm((p) => ({ ...p, causaMotivoAtencion: e.target.value }))
                                            }
                                            options={causas}
                                            placeholder="Seleccione causa…"
                                            menuZIndex={20500}
                                        />
                                    </div>
                                )}

                                {tipo === "consulta" ? (
                                    <>
                                        {campoCie("codDiagnosticoPrincipal", "CIE10 principal")}
                                        {campoCie("codDiagnosticoRelacionado1", "CIE10 relacionado 1")}
                                        {campoCie("codDiagnosticoRelacionado2", "CIE10 relacionado 2")}
                                        {campoCie("codDiagnosticoRelacionado3", "CIE10 relacionado 3")}
                                    </>
                                ) : (
                                    <>
                                        {campoCie("codDiagnosticoPrincipal", "CIE10 principal")}
                                        {campoCie("codDiagnosticoRelacionado", "CIE10 relacionado")}
                                        {campoCie("codComplicacion", "CIE10 complicación")}
                                    </>
                                )}

                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label>Concepto recaudo</label>
                                            <select
                                                className="form-control"
                                                value={form.conceptoRecaudo}
                                                onChange={(e) =>
                                                    setForm((p) => ({ ...p, conceptoRecaudo: e.target.value }))
                                                }
                                            >
                                                <option value="01">CUOTA MODERADORA</option>
                                                <option value="02">COPAGO</option>
                                                <option value="03">ANTICIPO</option>
                                                <option value="04">CUOTA DE RECUPERACIÓN</option>
                                                <option value="05">NO APLICA</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label>Pago moderador</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                value={form.valorPagoModerador}
                                                onChange={(e) =>
                                                    setForm((p) => ({ ...p, valorPagoModerador: e.target.value }))
                                                }
                                                onBlur={(e) => {
                                                    const v = e.target.value;
                                                    if (v === "" || v == null || !Number.isFinite(Number(v)) || Number(v) < 0) {
                                                        setForm((p) => ({ ...p, valorPagoModerador: "0" }));
                                                    }
                                                }}
                                                min="0"
                                                step="0.01"
                                                placeholder="0"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer" style={{ flex: "0 0 auto" }}>
                                <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn btn-info" disabled={saving}>
                                    {saving ? "Guardando…" : "Guardar plantilla"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <ModalCIE10
                show={showCie10}
                onClose={() => setShowCie10(false)}
                onSelect={(codigo) => {
                    setForm((p) => ({ ...p, [cie10Field]: codigo }));
                    setShowCie10(false);
                }}
                zIndex={20200}
            />
        </>,
        document.body
    );
}

export default ModalPlantillaRipsRapida;
